import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Alert,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Box,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Collapse,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import Page from 'src/components/page/Page';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import FileIcon from 'src/components/documents/FileIcon';
import {
  useGetDocumentWithOCR,
  OCRResultDTO,
} from 'src/api/core/documents/useGetDocument';
import useProcessDocumentOCR from 'src/api/core/documents/useProcessDocumentOCR';
import { useToast } from 'src/context/ToastContext';

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Unknown';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

const DocumentDetailPage: React.FC = () => {
  const { documentId } = useParams({
    from: '/authenticated/documents/$documentId',
  });
  const navigate = useNavigate();
  const { showToast } = useToast();

  const {
    data: documentData,
    isLoading,
    error,
    refetch,
  } = useGetDocumentWithOCR(documentId);

  const processOCR = useProcessDocumentOCR(documentId || '');

  const document = documentData?.document;
  const signedUrl = documentData?.signedUrl;
  const ocrResults = documentData?.ocrResults || [];

  const [activeMode, setActiveMode] = useState<'ocr' | 'extract'>('ocr');
  const [ocrInstructions, setOcrInstructions] = useState('');
  const [extractSchema, setExtractSchema] = useState('');
  const [responseFormat, setResponseFormat] = useState<
    'text' | 'json' | 'mixed'
  >('json');
  const [isRunningExtract, setIsRunningExtract] = useState(false);
  const [showOcrControls, setShowOcrControls] = useState(false);

  // Memoize the PDF URL to prevent unnecessary refreshes
  const stablePdfUrl = useMemo(() => {
    if (!signedUrl) return null;
    return `${signedUrl}#toolbar=0&navpanes=0&scrollbar=0`;
  }, [signedUrl]);

  const handleBack = () => {
    navigate({ to: '/documents' });
  };

  const handleDownload = async () => {
    try {
      if (signedUrl) {
        window.open(signedUrl, '_blank');
        showToast({
          message: `Opening ${document?.name}`,
          title: 'Download Started',
          color: 'info',
          icon: 'download',
        });
      }
    } catch (error) {
      showToast({
        message: `Failed to download ${document?.name}`,
        title: 'Download Failed',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleRunOCR = async () => {
    try {
      showToast({
        message: 'OCR processing started...',
        title: 'OCR Started',
        color: 'info',
        icon: 'text_fields',
      });

      await processOCR.mutateAsync({
        customInstructions: ocrInstructions || undefined,
        responseFormat,
      });

      showToast({
        message: `OCR processing started`,
        title: 'OCR Started',
        color: 'success',
        icon: 'check',
      });

      // Refresh document data to get updated OCR results
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (error) {
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to start OCR processing',
        title: 'OCR Failed',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleRunExtract = async () => {
    try {
      setIsRunningExtract(true);
      showToast({
        message: 'Data extraction started...',
        title: 'Extraction Started',
        color: 'info',
        icon: 'auto_awesome',
      });

      // TODO: Implement data extraction API call with schema
      console.log(
        'Running extraction on document:',
        documentId,
        'with schema:',
        extractSchema,
      );

      setTimeout(() => {
        setIsRunningExtract(false);
        showToast({
          message: 'Data extraction completed',
          title: 'Extraction Complete',
          color: 'success',
          icon: 'check',
        });
      }, 2000);
    } catch (error) {
      setIsRunningExtract(false);
      showToast({
        message: 'Failed to extract data',
        title: 'Extraction Failed',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        // TODO: Implement delete API call
        console.log('Deleting document:', documentId);

        showToast({
          message: 'Document deleted successfully',
          title: 'Success',
          color: 'success',
          icon: 'check',
        });

        navigate({ to: '/documents' });
      } catch (error) {
        showToast({
          message: 'Failed to delete document',
          title: 'Error',
          color: 'error',
          icon: 'error',
        });
      }
    }
  };

  const formatOCRResult = (ocrResult: OCRResultDTO) => {
    // First try to get the raw result
    const data = ocrResult.result;

    // If it's a string, try to parse it as JSON
    if (typeof data === 'string') {
      try {
        // Try to parse as JSON
        const parsed = JSON.parse(data);
        return parsed;
      } catch {
        // If parsing fails, return the string as-is
        return data;
      }
    }

    // If it's already an object, return it
    if (typeof data === 'object' && data !== null) {
      return data;
    }

    return 'No result available';
  };

  type JsonData =
    | string
    | number
    | boolean
    | null
    | JsonData[]
    | { [key: string]: JsonData };

  const JsonViewer: React.FC<{ data: JsonData; level?: number }> = ({
    data,
    level = 0,
  }) => {
    const [expanded, setExpanded] = useState(level < 2);

    if (data === null)
      return (
        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>null</span>
      );
    if (data === undefined)
      return (
        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>undefined</span>
      );

    if (typeof data === 'string') {
      return (
        <span
          style={{
            color: '#198754',
            wordBreak: 'break-word',
            fontSize: '0.8rem',
          }}
        >
          &quot;{data}&quot;
        </span>
      );
    }

    if (typeof data === 'number') {
      return (
        <span
          style={{ color: '#0d6efd', fontWeight: 'bold', fontSize: '0.8rem' }}
        >
          {data}
        </span>
      );
    }

    if (typeof data === 'boolean') {
      return (
        <span
          style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '0.8rem' }}
        >
          {data.toString()}
        </span>
      );
    }

    if (Array.isArray(data)) {
      if (data.length === 0)
        return <span style={{ color: '#6c757d' }}>[]</span>;

      return (
        <Box>
          <Icon
            onClick={() => setExpanded(!expanded)}
            sx={{
              fontSize: 14,
              color: '#6c757d',
              cursor: 'pointer',
              '&:hover': { color: '#495057' },
            }}
          >
            {expanded ? 'expand_less' : 'expand_more'}
          </Icon>
          <Collapse in={expanded}>
            <Box
              sx={{
                ml: 0.5,
                borderLeft: '1px solid #e9ecef',
                pl: 0.5,
                mt: 0.25,
                '& > *:not(:last-child)': { mb: 0.25 },
              }}
            >
              {data.map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <span
                    style={{
                      color: '#6c757d',
                      fontSize: '0.7rem',
                      minWidth: '14px',
                      marginRight: '4px',
                      marginTop: '0px',
                    }}
                  >
                    {index}:
                  </span>
                  <Box sx={{ flex: 1 }}>
                    <JsonViewer data={item} level={level + 1} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      );
    }

    if (typeof data === 'object') {
      const keys = Object.keys(data);
      if (keys.length === 0)
        return <span style={{ color: '#6c757d' }}>{'{}'}</span>;

      return (
        <Box>
          <Icon
            onClick={() => setExpanded(!expanded)}
            sx={{
              fontSize: 14,
              color: '#6c757d',
              cursor: 'pointer',
              '&:hover': { color: '#495057' },
            }}
          >
            {expanded ? 'expand_less' : 'expand_more'}
          </Icon>
          <Collapse in={expanded}>
            <Box
              sx={{
                ml: 0.5,
                borderLeft: '1px solid #e9ecef',
                pl: 0.5,
                mt: 0.25,
                '& > *:not(:last-child)': { mb: 0.25 },
              }}
            >
              {keys.map((key) => (
                <Box
                  key={key}
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <span
                    style={{
                      color: '#e83e8c',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      minWidth: 'fit-content',
                      marginRight: '4px',
                      marginTop: '0px',
                    }}
                  >
                    &quot;{key}&quot;:
                  </span>
                  <Box sx={{ flex: 1 }}>
                    <JsonViewer data={data[key]} level={level + 1} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      );
    }

    return <span style={{ color: '#6c757d' }}>{String(data)}</span>;
  };

  const OCRResultViewer: React.FC<{ result: OCRResultDTO }> = ({ result }) => {
    const [copied, setCopied] = useState(false);

    // Parse the result data
    const parsedData = formatOCRResult(result);
    const isJsonData = typeof parsedData === 'object' && parsedData !== null;
    const isStringData = typeof parsedData === 'string';

    const copyToClipboard = () => {
      const text = isJsonData
        ? JSON.stringify(parsedData, null, 2)
        : String(parsedData);

      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };

    return (
      <Box>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          m={2}
        >
          <Typography variant="body2" fontWeight="medium" color="text.primary">
            Data structure: {isJsonData ? 'JSON' : 'Text'}
          </Typography>
          <IconButton
            size="small"
            onClick={copyToClipboard}
            sx={{
              opacity: 0.7,
              '&:hover': { opacity: 1 },
              backgroundColor: copied ? '#e8f5e8' : 'transparent',
            }}
          >
            <Icon sx={{ fontSize: 16, color: copied ? '#198754' : 'inherit' }}>
              {copied ? 'check' : 'content_copy'}
            </Icon>
          </IconButton>
        </MDBox>

        <Box
          sx={{
            maxHeight: 400,
            overflow: 'auto',
            backgroundColor: '#fafafa',
            p: 3,
            borderRadius: 2,
            border: '1px solid #e9ecef',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          }}
        >
          {isJsonData ? (
            <JsonViewer data={parsedData} />
          ) : isStringData ? (
            <Typography
              component="pre"
              sx={{
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                lineHeight: 1.6,
                color: '#198754',
              }}
            >
              {parsedData}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              No result available
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const getOCRStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'processing':
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Page title="Document Details">
        <Alert severity="error" sx={{ mb: 4 }}>
          {error.message || 'Failed to load document'}
        </Alert>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page title="Document Details">
        <MDBox p={3}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Skeleton variant="rectangular" height={600} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Skeleton variant="rectangular" height={600} />
            </Grid>
          </Grid>
        </MDBox>
      </Page>
    );
  }

  if (!document) {
    return (
      <Page title="Document Details">
        <Alert severity="warning" sx={{ mb: 4 }}>
          Document not found
        </Alert>
      </Page>
    );
  }

  return (
    <Page title={`Document`}>
      <MDBox>
        {/* Header */}
        <Card sx={{ py: 2, mb: 3 }}>
          <CardContent>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <MDBox display="flex" alignItems="center">
                <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                  <Icon>arrow_back</Icon>
                </IconButton>
                <FileIcon
                  fileName={document?.name}
                  contentType={document?.mimeType}
                  size="medium"
                />
                <MDBox ml={2}>
                  <MDTypography variant="h4" fontWeight="medium" mb={0.5}>
                    {document?.name}
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="body2" color="text">
                      {formatFileSize(document?.fileSizeBytes)}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Uploaded {formatDate(document?.createdAt)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox display="flex" gap={1}>
                <Tooltip title="Download">
                  <IconButton
                    color="info"
                    onClick={handleDownload}
                    size="small"
                  >
                    <Icon>download</Icon>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" onClick={handleDelete} size="small">
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Document Viewer */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '80vh' }}>
              <CardContent sx={{ p: 0, height: '100%' }}>
                <MDBox p={2} borderBottom="1px solid #e0e0e0">
                  <MDTypography variant="h6" fontWeight="medium">
                    Document Preview
                  </MDTypography>
                </MDBox>

                <MDBox
                  sx={{ height: 'calc(100% - 60px)', position: 'relative' }}
                >
                  {stablePdfUrl ? (
                    <iframe
                      key={documentId}
                      src={stablePdfUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      title={document?.name}
                    />
                  ) : (
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                      color="text.secondary"
                    >
                      <Icon sx={{ fontSize: 64, mb: 2, color: '#bdbdbd' }}>
                        visibility_off
                      </Icon>
                      <MDTypography variant="h6" color="text" mb={1}>
                        Preview not available
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        Document preview is not supported for this file type
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions Panel */}
          <Grid item xs={12} lg={6}>
            {/* OCR/Extraction Mode */}
            <Card sx={{ pt: 2 }}>
              <CardContent>
                {/* Mode Toggle */}
                <MDBox mb={3}>
                  <ToggleButtonGroup
                    value={activeMode}
                    exclusive
                    onChange={(_, newMode) => newMode && setActiveMode(newMode)}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton value="ocr">
                      <Icon sx={{ mr: 1 }}>text_fields</Icon>
                      OCR
                    </ToggleButton>
                    <ToggleButton value="extract">
                      <Icon sx={{ mr: 1 }}>auto_awesome</Icon>
                      Extract
                    </ToggleButton>
                  </ToggleButtonGroup>
                </MDBox>

                {/* OCR Mode */}
                {activeMode === 'ocr' && (
                  <MDBox>
                    {/* Toggle Button for OCR Controls */}
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => setShowOcrControls(!showOcrControls)}
                        startIcon={
                          <Icon>
                            {showOcrControls ? 'expand_less' : 'expand_more'}
                          </Icon>
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        {showOcrControls ? 'Hide' : 'Run'} OCR Options
                      </MDButton>
                    </MDBox>

                    {/* Collapsible OCR Controls */}
                    <Collapse in={showOcrControls}>
                      <MDBox mb={3}>
                        <TextField
                          fullWidth
                          label="Special Instructions"
                          placeholder="e.g., This is an invoice, focus on line items..."
                          value={ocrInstructions}
                          onChange={(e) => setOcrInstructions(e.target.value)}
                          multiline
                          rows={3}
                          size="small"
                          sx={{ mb: 2 }}
                        />

                        <MDBox display="flex" gap={1} mb={2}>
                          <TextField
                            select
                            label="Response Format"
                            value={responseFormat}
                            onChange={(e) =>
                              setResponseFormat(
                                e.target.value as 'text' | 'json' | 'mixed',
                              )
                            }
                            size="small"
                            SelectProps={{ native: true }}
                            sx={{ flex: 1 }}
                          >
                            <option value="text">Text Only</option>
                            <option value="json">JSON Only</option>
                            <option value="mixed">Mixed</option>
                          </TextField>
                        </MDBox>

                        <MDButton
                          variant="gradient"
                          color="info"
                          fullWidth
                          onClick={handleRunOCR}
                          disabled={processOCR.isLoading}
                          startIcon={<Icon>text_fields</Icon>}
                        >
                          {processOCR.isLoading ? 'Processing...' : 'Run OCR'}
                        </MDButton>
                      </MDBox>
                    </Collapse>

                    {ocrResults.length > 0 && (
                      <MDBox mt={3}>
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="medium"
                          mb={2}
                        >
                          OCR Results ({ocrResults.length})
                        </MDTypography>

                        <Box
                          sx={{ maxHeight: '60vh', overflow: 'auto', pr: 1 }}
                        >
                          {ocrResults.map((result, index) => (
                            <Paper
                              key={result.id}
                              elevation={1}
                              sx={{
                                mb: 2,
                                p: 2,
                                border: '1px solid #e0e0e0',
                                '&:last-child': { mb: 0 },
                              }}
                            >
                              {/* Header */}
                              <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  color="text.primary"
                                >
                                  Result #{index + 1}
                                </Typography>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {result.vendor} •{' '}
                                    {new Date(
                                      result.createdAt,
                                    ).toLocaleDateString()}
                                  </Typography>
                                  <Chip
                                    label={result.status}
                                    size="small"
                                    color={getOCRStatusColor(result.status)}
                                    variant="outlined"
                                  />
                                </MDBox>
                              </MDBox>

                              {/* Custom Instructions */}
                              {result.customInstructions && (
                                <MDBox mb={2}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Instructions:
                                  </Typography>
                                  <Box
                                    sx={{
                                      backgroundColor: '#f8f9fa',
                                      p: 1.5,
                                      borderRadius: 1,
                                      border: '1px solid #e9ecef',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '0.875rem',
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      &ldquo;{result.customInstructions}&rdquo;
                                    </Typography>
                                  </Box>
                                </MDBox>
                              )}

                              {/* Error Display */}
                              {result.error ? (
                                <Alert
                                  severity="error"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {result.error}
                                </Alert>
                              ) : (
                                /* OCR Results */
                                <Accordion
                                  defaultExpanded={index === 0}
                                  sx={{
                                    boxShadow: 'none',
                                    border: '1px solid #e0e0e0',
                                    '&:before': { display: 'none' },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<Icon>expand_more</Icon>}
                                    sx={{
                                      backgroundColor: '#fafafa',
                                      minHeight: 40,
                                      '& .MuiAccordionSummary-content': {
                                        margin: '8px 0',
                                      },
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      Extracted Content
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ p: 0 }}>
                                    <OCRResultViewer result={result} />
                                  </AccordionDetails>
                                </Accordion>
                              )}

                              {/* Token Usage */}
                              {result.tokenUsage && (
                                <MDBox
                                  mt={2}
                                  pt={2}
                                  sx={{ borderTop: '1px solid #e0e0e0' }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    <strong>Tokens:</strong>{' '}
                                    {result.tokenUsage.totalTokens || 0} total (
                                    {result.tokenUsage.promptTokens || 0} prompt
                                    + {result.tokenUsage.completionTokens || 0}{' '}
                                    completion)
                                  </Typography>
                                </MDBox>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      </MDBox>
                    )}

                    {ocrResults.length === 0 && (
                      <MDBox mt={3} textAlign="center" color="text.secondary">
                        <MDTypography variant="body2" color="text">
                          No OCR results yet. Run OCR to extract text from this
                          document.
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )}

                {/* Extract Mode */}
                {activeMode === 'extract' && (
                  <MDBox>
                    <TextField
                      disabled
                      fullWidth
                      label="Data Schema (coming soon)"
                      placeholder="JSON schema or describe what to extract..."
                      value={extractSchema}
                      onChange={(e) => setExtractSchema(e.target.value)}
                      multiline
                      rows={6}
                      size="small"
                      sx={{ mb: 2 }}
                      helperText="Define JSON schema or describe the data you want to extract"
                    />

                    <MDButton
                      variant="gradient"
                      color="success"
                      fullWidth
                      onClick={handleRunExtract}
                      // disabled={isRunningExtract || !extractSchema.trim()}
                      disabled
                      startIcon={<Icon>auto_awesome</Icon>}
                    >
                      {isRunningExtract ? 'Extracting...' : 'Extract Data'}
                    </MDButton>
                  </MDBox>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </Page>
  );
};

export default DocumentDetailPage;
